import clsx from 'clsx'
import { rem } from 'polished'
import { memo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import NestableClickable from 'components/Common/NestableClickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { INTL_DMY_CASUAL_SHORT_FORMAT, INTL_SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { useAirlineByCode, useAirport } from 'tripPlanner/hooks/api/flights'
import { concatStringWithDot } from 'tripPlanner/utils'
import type { FlightSchedule } from 'tripPlanner/types/common'

interface Props {
  flight: FlightSchedule
  showSelect?: boolean
  selected?: boolean
  onClick: (flightKey: string) => void
}

const Card = styled(NestableClickable)`
  display: grid;
  border-radius: ${({ theme }) => theme.borderRadius.M};
  border: 1px solid ${({ theme }) => theme.palette.neutral.surface.lightForeground};
  gap: ${rem(12)};
  width: 100%;
  padding: ${rem(16)};
  background-color: ${({ theme }) => theme.palette.neutral.surface.normalBackground};
  break-inside: avoid-page;

  &.selected {
    border-color: ${({ theme }) => theme.palette.brand.primary.normal};
  }
`

const CardLoader = styled(LoadingBox)`
  min-height: ${rem(120)};
  width: 100%;
`

const Logo = styled.img`
  height: ${rem(24)};
`

/** Flight lookup displays a schedule directly; it does not create a classic trip item. */
function FlightLookupTile({ flight, showSelect, selected, onClick: onClickProp }: Props) {
  const intl = useIntl()
  const { data: departureAirport, isLoading: departureAirportLoading } = useAirport({ code: flight.departureAirportCode })
  const { data: arrivalAirport, isLoading: arrivalAirportLoading } = useAirport({ code: flight.arrivalAirportCode })
  const { data: airline } = useAirlineByCode({ code: flight.airlineCode })
  const onClick = useCallback(() => onClickProp(flight.key), [onClickProp, flight.key])

  if (departureAirportLoading || arrivalAirportLoading) return <CardLoader />
  if (!departureAirport || !arrivalAirport) return null

  const origin = departureAirport.airportName || departureAirport.cityAirportName || departureAirport.cityDesc
  const destination = arrivalAirport.airportName || arrivalAirport.cityAirportName || arrivalAirport.cityDesc
  const route = intl.formatMessage({
    id: 'vo5xzjanMP9BfdN-3y6q',
    defaultMessage: '{origin} to {destination}',
    description: 'Flight route heading showing departure to arrival airport names',
  }, { origin, destination })
  const icon = airline?.airlineLogo ? <Logo src={airline.airlineLogo} alt="" /> : <LinePlaneFlyIcon size="XS" />

  return (
    <Group direction="horizontal" gap={8} verticalAlign="center">
      {showSelect && <CircleCheckboxInput onClick={onClick} checked={!!selected} aria-label={route} />}
      <Card className={clsx({ selected: showSelect && !!selected })} onClick={showSelect ? onClick : undefined}>
        <BodyTextBlock variant="small" startIcon={icon}>
          {concatStringWithDot(flight.airlineName, `${flight.airlineCode} ${flight.flightNumber}`)}
        </BodyTextBlock>
        <Heading as="h3" variant="heading6">{route}</Heading>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
          <VerticalSpacer gap={4}>
            <BodyText variant="small">{intlDateFormatter(flight.departureDate, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)}</BodyText>
            <Heading variant="heading6">{intlDateFormatter(flight.departureTime, INTL_SHORT_TIME_FORMAT_24_HOUR, intl.locale)}</Heading>
            <BodyText variant="medium" colour="light-foreground-alt">{flight.departureAirportCode}</BodyText>
          </VerticalSpacer>
          <LinePlaneFlyIcon />
          <VerticalSpacer gap={4}>
            <BodyText variant="small">{intlDateFormatter(flight.arrivalDate, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)}</BodyText>
            <Heading variant="heading6">{intlDateFormatter(flight.arrivalTime, INTL_SHORT_TIME_FORMAT_24_HOUR, intl.locale)}</Heading>
            <BodyText variant="medium" colour="light-foreground-alt">{flight.arrivalAirportCode}</BodyText>
          </VerticalSpacer>
        </Group>
      </Card>
    </Group>
  )
}

export default memo(FlightLookupTile)
